/**
 * Campaigns Domain Seed Data
 *
 * Contains all seed data for CRM Service (34) campaign features:
 * - Email Campaigns (Resend integration)
 * - Voice Campaigns (Retell AI integration)
 * - Voice Calls
 * - Call Intelligence
 * - AI Autonomy Settings
 * - Communication Preferences
 *
 * @see PR #265 - Outbound Sales Motion
 */

import { seedId } from '../utils';
import { LEAD_IDS } from './crm';
import { BUSINESS_IDS } from './tenants';

// =============================================================================
// EMAIL CAMPAIGN IDs
// =============================================================================

export const EMAIL_CAMPAIGN_IDS = {
  // Fiops Capital campaigns
  FUNDING_NURTURE_Q1: seedId('email-campaign-funding-nurture', 1),
  FUNDING_REENGAGEMENT: seedId('email-campaign-funding-reengage', 2),
  FUNDING_NEW_PRODUCTS: seedId('email-campaign-funding-products', 3),

  // 1Sharpe Capital campaigns
  SHARPE_WELCOME_SERIES: seedId('email-campaign-sharpe-welcome', 1),
  SHARPE_BRIDGE_PROMO: seedId('email-campaign-sharpe-bridge', 2),

  // Pacific Bridge Fund campaigns
  PACIFIC_INVESTOR_UPDATE: seedId('email-campaign-pacific-update', 1),
} as const;

export type EmailCampaignId = (typeof EMAIL_CAMPAIGN_IDS)[keyof typeof EMAIL_CAMPAIGN_IDS];

// =============================================================================
// VOICE CAMPAIGN IDs
// =============================================================================

export const VOICE_CAMPAIGN_IDS = {
  // Fiops Capital voice campaigns
  FUNDING_NEW_LEAD_OUTREACH: seedId('voice-campaign-funding-outreach', 1),
  FUNDING_FOLLOWUP: seedId('voice-campaign-funding-followup', 2),
  VOICE_INVESTOR_OUTREACH: seedId('voice-campaign-investor-outreach', 3),
  VOICE_FOLLOW_UP: seedId('voice-campaign-voice-followup', 4),

  // 1Sharpe Capital voice campaigns
  SHARPE_QUALIFICATION_CALLS: seedId('voice-campaign-sharpe-qual', 1),
} as const;

export type VoiceCampaignId = (typeof VOICE_CAMPAIGN_IDS)[keyof typeof VOICE_CAMPAIGN_IDS];

// =============================================================================
// VOICE CALL IDs
// =============================================================================

export const VOICE_CALL_IDS = {
  // Sample voice calls
  CALL_MARCUS_INTRO: seedId('voice-call-marcus', 1),
  CALL_ROBERT_FOLLOWUP: seedId('voice-call-robert', 1),
  CALL_ALICIA_DSCR: seedId('voice-call-alicia', 1),
  CALL_JENNIFER_FIRST: seedId('voice-call-jennifer', 1),
  CALL_KEVIN_PREQUAL: seedId('voice-call-kevin', 1),
} as const;

export type VoiceCallId = (typeof VOICE_CALL_IDS)[keyof typeof VOICE_CALL_IDS];

// =============================================================================
// CALL INTELLIGENCE IDs
// =============================================================================

export const CALL_INTELLIGENCE_IDS = {
  INTEL_MARCUS_INTRO: seedId('call-intel-marcus', 1),
  INTEL_ALICIA_DSCR: seedId('call-intel-alicia', 1),
  INTEL_JENNIFER_FIRST: seedId('call-intel-jennifer', 1),
} as const;

export type CallIntelligenceId = (typeof CALL_INTELLIGENCE_IDS)[keyof typeof CALL_INTELLIGENCE_IDS];

// =============================================================================
// AI AUTONOMY SETTINGS IDs
// =============================================================================

export const AI_AUTONOMY_SETTINGS_IDS = {
  FUNDING_AUTONOMY: seedId('ai-autonomy-funding', 1),
  SHARPE_AUTONOMY: seedId('ai-autonomy-sharpe', 1),
  PACIFIC_AUTONOMY: seedId('ai-autonomy-pacific', 1),
} as const;

export type AIAutonomySettingsId = (typeof AI_AUTONOMY_SETTINGS_IDS)[keyof typeof AI_AUTONOMY_SETTINGS_IDS];

// =============================================================================
// DATA TYPES
// =============================================================================

export interface EmailCampaignSeedData {
  id: string;
  businessId: string;
  name: string;
  subject: string;
  previewText?: string;
  status: 'draft' | 'scheduled' | 'active' | 'paused' | 'completed';
  recipientType: 'lead' | 'partner' | 'segment';
  htmlContent: string;
  textContent: string;
  filterCriteria?: Record<string, unknown>;
  scheduledAt?: string;
}

export interface VoiceCampaignSeedData {
  id: string;
  businessId: string;
  name: string;
  description?: string;
  status: 'draft' | 'scheduled' | 'active' | 'paused' | 'completed';
  retellAgentId?: string;
  voiceId?: string;
  fromNumber?: string;
  aiAutonomyLevel: 'full_ai' | 'hybrid' | 'ai_assist' | 'human_only';
  greeting: string;
  prompt: string;
  maxCallDuration?: number;
  callWindowStart?: string; // HH:MM format
  callWindowEnd?: string;
  timezone?: string;
  respectDnc: boolean;
}

export interface VoiceCallSeedData {
  id: string;
  businessId: string;
  campaignId: string;
  recipientId: string;
  recipientType: 'lead' | 'partner' | 'consumer';
  status: 'pending' | 'ringing' | 'in_progress' | 'completed' | 'failed' | 'voicemail';
  outcome?: 'qualified' | 'not_interested' | 'callback' | 'transferred' | 'voicemail' | 'no_answer';
  duration?: number;
  recordingUrl?: string;
  transcription?: string;
  sentiment?: 'positive' | 'neutral' | 'negative';
}

export interface CallIntelligenceSeedData {
  id: string;
  businessId: string;
  callId: string;
  borrowerIntent?: 'purchase' | 'refinance' | 'cash_out' | 'construction';
  propertyType?: string;
  timeline?: string;
  loanAmountRange?: { min: number; max: number };
  qualificationScore?: number;
  qualificationFactors?: string[];
  competitorMentions?: string[];
  objections?: string[];
  nextActions?: string[];
}

export interface AIAutonomySettingsSeedData {
  id: string;
  businessId: string;
  defaultAutonomyLevel: 'full_ai' | 'hybrid' | 'ai_assist' | 'human_only';
  escalationRules: EscalationRule[];
  humanAvailabilitySchedule?: Record<string, unknown>;
  fallbackBehavior: 'voicemail' | 'callback' | 'end_call';
  enableLearning: boolean;
}

export interface EscalationRule {
  trigger: 'keyword' | 'loan_amount' | 'product_type' | 'sentiment' | 'objection_count' | 'confidence';
  condition: string;
  threshold?: number;
  action: 'transfer' | 'flag' | 'notify' | 'end_call';
}

// =============================================================================
// EMAIL CAMPAIGN DATA
// =============================================================================

export const EMAIL_CAMPAIGNS: Record<keyof typeof EMAIL_CAMPAIGN_IDS, EmailCampaignSeedData> = {
  FUNDING_NURTURE_Q1: {
    id: EMAIL_CAMPAIGN_IDS.FUNDING_NURTURE_Q1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Q1 2026 Nurture Sequence',
    subject: 'Unlock Your Next Investment Opportunity',
    previewText: 'New funding options available for experienced investors',
    status: 'active',
    recipientType: 'lead',
    htmlContent: `<h1>Investment Opportunities Await</h1>
<p>Dear {{firstName}},</p>
<p>As an experienced investor, you know that timing is everything in real estate. Our Q1 rates are now available.</p>
<p>Current Rates:</p>
<ul>
  <li>Fix & Flip: Starting at 9.9%</li>
  <li>Bridge Loans: Starting at 10.5%</li>
  <li>DSCR: Starting at 7.5%</li>
</ul>
<p>Reply to schedule a call with your dedicated loan officer.</p>`,
    textContent:
      'Investment Opportunities Await. Our Q1 rates are now available. Fix & Flip starting at 9.9%, Bridge at 10.5%, DSCR at 7.5%.',
  },
  FUNDING_REENGAGEMENT: {
    id: EMAIL_CAMPAIGN_IDS.FUNDING_REENGAGEMENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Lead Re-engagement Campaign',
    subject: "We haven't heard from you - new rates inside",
    previewText: 'Your pre-approval is waiting',
    status: 'draft',
    recipientType: 'lead',
    htmlContent: `<h1>Still Thinking About Your Next Deal?</h1>
<p>Hi {{firstName}},</p>
<p>We noticed you haven't submitted an application yet. Ready to move forward? Your pre-approval could be ready in 24 hours.</p>`,
    textContent: 'Still thinking about your next deal? Your pre-approval could be ready in 24 hours.',
    filterCriteria: { lastActivityDaysAgo: { gte: 30 }, status: 'inactive' },
  },
  FUNDING_NEW_PRODUCTS: {
    id: EMAIL_CAMPAIGN_IDS.FUNDING_NEW_PRODUCTS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'New Product Announcement',
    subject: 'Introducing: Ground-Up Construction Loans',
    status: 'scheduled',
    recipientType: 'segment',
    htmlContent: `<h1>New: Ground-Up Construction Financing</h1>
<p>We're excited to announce our new construction loan product.</p>`,
    textContent: 'Introducing ground-up construction loans. Contact us for details.',
    scheduledAt: '2026-02-01T09:00:00Z',
  },
  SHARPE_WELCOME_SERIES: {
    id: EMAIL_CAMPAIGN_IDS.SHARPE_WELCOME_SERIES,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'New Lead Welcome Series',
    subject: 'Welcome to 1Sharpe Capital',
    status: 'active',
    recipientType: 'lead',
    htmlContent: `<h1>Welcome to 1Sharpe Capital</h1>
<p>Thank you for your interest in our lending solutions.</p>`,
    textContent: 'Welcome to 1Sharpe Capital. Thank you for your interest.',
  },
  SHARPE_BRIDGE_PROMO: {
    id: EMAIL_CAMPAIGN_IDS.SHARPE_BRIDGE_PROMO,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Bridge Loan Promotion',
    subject: 'Limited Time: Bridge Loan Special',
    status: 'draft',
    recipientType: 'lead',
    htmlContent: '<h1>Bridge Loan Special</h1><p>Limited time offer on bridge financing.</p>',
    textContent: 'Limited time bridge loan special.',
  },
  PACIFIC_INVESTOR_UPDATE: {
    id: EMAIL_CAMPAIGN_IDS.PACIFIC_INVESTOR_UPDATE,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    name: 'Monthly Investor Update',
    subject: 'Pacific Bridge Fund - January Update',
    status: 'completed',
    recipientType: 'partner',
    htmlContent: '<h1>January 2026 Portfolio Update</h1><p>Fund performance and outlook.</p>',
    textContent: 'January 2026 portfolio update from Pacific Bridge Fund.',
  },
};

// =============================================================================
// VOICE CAMPAIGN DATA
// =============================================================================

export const VOICE_CAMPAIGNS: Record<keyof typeof VOICE_CAMPAIGN_IDS, VoiceCampaignSeedData> = {
  FUNDING_NEW_LEAD_OUTREACH: {
    id: VOICE_CAMPAIGN_IDS.FUNDING_NEW_LEAD_OUTREACH,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'New Lead Outreach',
    description: 'AI-powered outreach to new leads within 24 hours of submission',
    status: 'active',
    aiAutonomyLevel: 'hybrid',
    greeting:
      "Hello, this is Sarah calling from Fiops Capital. I'm reaching out about your recent inquiry for investment property financing. Is this a good time to chat for a few minutes?",
    prompt: `You are Sarah, a professional loan officer assistant at Fiops Capital. Your goal is to:
1. Qualify the lead by understanding their investment experience and current project
2. Gather basic loan requirements (property type, location, loan amount, timeline)
3. Determine if they're a good fit for our products
4. Schedule a follow-up call with a loan officer if qualified

Be conversational, professional, and helpful. If they mention a loan amount over $2M or complex deal structure, offer to transfer to a senior loan officer.`,
    maxCallDuration: 600,
    callWindowStart: '09:00',
    callWindowEnd: '18:00',
    timezone: 'America/Chicago',
    respectDnc: true,
  },
  FUNDING_FOLLOWUP: {
    id: VOICE_CAMPAIGN_IDS.FUNDING_FOLLOWUP,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Application Follow-up',
    description: 'Follow-up calls for incomplete applications',
    status: 'draft',
    aiAutonomyLevel: 'ai_assist',
    greeting:
      'Hi, this is a quick follow-up from Fiops Capital about your loan application. We noticed a few items are still needed.',
    prompt: 'Help the borrower complete their application by identifying missing documents and answering questions.',
    respectDnc: true,
  },
  VOICE_INVESTOR_OUTREACH: {
    id: VOICE_CAMPAIGN_IDS.VOICE_INVESTOR_OUTREACH,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Investor Portfolio Outreach',
    description: 'AI-driven outreach to existing investors about new portfolio opportunities and market insights',
    status: 'active',
    retellAgentId: 'agent-investor-outreach',
    voiceId: 'david',
    fromNumber: '+1-512-555-9001',
    aiAutonomyLevel: 'hybrid',
    greeting:
      "Good morning, this is David from Fiops Capital's investor relations team. I'm calling with some exciting portfolio opportunities I think would be a great fit for your investment strategy. Do you have a few minutes?",
    prompt: `You are David, a senior investor relations associate at Fiops Capital. Your goal is to:
1. Review the investor's current portfolio with Fiops
2. Present 1-2 new investment opportunities that match their profile
3. Discuss current market conditions and how they affect returns
4. Gauge interest in expanding their portfolio or diversifying
5. Schedule a detailed portfolio review with a senior advisor if interested

Be professional, knowledgeable about real estate investment, and data-driven. Reference specific market metrics when possible. If the investor mentions amounts over $5M, offer to connect with the managing director.`,
    maxCallDuration: 900,
    callWindowStart: '09:00',
    callWindowEnd: '17:00',
    timezone: 'America/Chicago',
    respectDnc: true,
  },
  VOICE_FOLLOW_UP: {
    id: VOICE_CAMPAIGN_IDS.VOICE_FOLLOW_UP,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Post-Closing Follow-Up',
    description:
      'Automated follow-up calls to borrowers after loan closing for satisfaction and referral opportunities',
    status: 'scheduled',
    retellAgentId: 'agent-followup-satisfaction',
    voiceId: 'sarah',
    fromNumber: '+1-512-555-9002',
    aiAutonomyLevel: 'ai_assist',
    greeting:
      'Hi, this is Sarah from Fiops Capital. I wanted to follow up on your recent loan closing and make sure everything went smoothly. Do you have a moment?',
    prompt: `You are Sarah, a client success associate at Fiops Capital. Your goal is to:
1. Congratulate the borrower on their recent closing
2. Ask about their experience with the loan process
3. Address any post-closing questions or concerns
4. Ask if they have any upcoming projects that might need financing
5. Request a referral if the experience was positive

Be warm, genuine, and appreciative. If they mention any issues, document them carefully and offer to have a manager follow up.`,
    maxCallDuration: 600,
    callWindowStart: '10:00',
    callWindowEnd: '18:00',
    timezone: 'America/Chicago',
    respectDnc: true,
  },
  SHARPE_QUALIFICATION_CALLS: {
    id: VOICE_CAMPAIGN_IDS.SHARPE_QUALIFICATION_CALLS,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Lead Qualification Calls',
    description: 'Initial qualification calls for new leads',
    status: 'active',
    aiAutonomyLevel: 'full_ai',
    greeting: 'Hello, this is 1Sharpe Capital calling about your financing inquiry.',
    prompt: 'Qualify leads for bridge and construction loan products.',
    callWindowStart: '10:00',
    callWindowEnd: '17:00',
    timezone: 'America/New_York',
    respectDnc: true,
  },
};

// =============================================================================
// VOICE CALL DATA
// =============================================================================

export const VOICE_CALLS: Record<keyof typeof VOICE_CALL_IDS, VoiceCallSeedData> = {
  CALL_MARCUS_INTRO: {
    id: VOICE_CALL_IDS.CALL_MARCUS_INTRO,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    campaignId: VOICE_CAMPAIGN_IDS.FUNDING_NEW_LEAD_OUTREACH,
    recipientId: LEAD_IDS.LEAD_MARCUS,
    recipientType: 'lead',
    status: 'completed',
    outcome: 'qualified',
    duration: 342,
    sentiment: 'positive',
    transcription: 'Sample transcription of successful qualification call...',
  },
  CALL_ROBERT_FOLLOWUP: {
    id: VOICE_CALL_IDS.CALL_ROBERT_FOLLOWUP,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    campaignId: VOICE_CAMPAIGN_IDS.FUNDING_FOLLOWUP,
    recipientId: LEAD_IDS.LEAD_ROBERT,
    recipientType: 'lead',
    status: 'completed',
    outcome: 'callback',
    duration: 180,
    sentiment: 'neutral',
  },
  CALL_ALICIA_DSCR: {
    id: VOICE_CALL_IDS.CALL_ALICIA_DSCR,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    campaignId: VOICE_CAMPAIGN_IDS.FUNDING_NEW_LEAD_OUTREACH,
    recipientId: LEAD_IDS.LEAD_ALICIA,
    recipientType: 'lead',
    status: 'completed',
    outcome: 'qualified',
    duration: 485,
    sentiment: 'positive',
  },
  CALL_JENNIFER_FIRST: {
    id: VOICE_CALL_IDS.CALL_JENNIFER_FIRST,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    campaignId: VOICE_CAMPAIGN_IDS.FUNDING_NEW_LEAD_OUTREACH,
    recipientId: LEAD_IDS.LEAD_JENNIFER,
    recipientType: 'lead',
    status: 'completed',
    outcome: 'transferred',
    duration: 245,
    sentiment: 'positive',
  },
  CALL_KEVIN_PREQUAL: {
    id: VOICE_CALL_IDS.CALL_KEVIN_PREQUAL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    campaignId: VOICE_CAMPAIGN_IDS.FUNDING_NEW_LEAD_OUTREACH,
    recipientId: LEAD_IDS.LEAD_KEVIN,
    recipientType: 'lead',
    status: 'voicemail',
    outcome: 'voicemail',
    duration: 45,
  },
};

// =============================================================================
// CALL INTELLIGENCE DATA
// =============================================================================

export const CALL_INTELLIGENCE: Record<keyof typeof CALL_INTELLIGENCE_IDS, CallIntelligenceSeedData> = {
  INTEL_MARCUS_INTRO: {
    id: CALL_INTELLIGENCE_IDS.INTEL_MARCUS_INTRO,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    callId: VOICE_CALL_IDS.CALL_MARCUS_INTRO,
    borrowerIntent: 'purchase',
    propertyType: 'Multifamily',
    timeline: '30 days',
    loanAmountRange: { min: 2000000, max: 2500000 },
    qualificationScore: 92,
    qualificationFactors: ['VIP client', '12 completed deals', 'Strong liquidity', 'Clear timeline'],
    nextActions: ['Schedule portfolio review', 'Prepare term sheet for 5-property package'],
  },
  INTEL_ALICIA_DSCR: {
    id: CALL_INTELLIGENCE_IDS.INTEL_ALICIA_DSCR,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    callId: VOICE_CALL_IDS.CALL_ALICIA_DSCR,
    borrowerIntent: 'purchase',
    propertyType: '4-plex',
    timeline: '45 days',
    loanAmountRange: { min: 400000, max: 500000 },
    qualificationScore: 85,
    qualificationFactors: ['Experienced investor', 'Strong cash flow', 'Tampa market knowledge'],
    competitorMentions: ['Kiavi', 'Lima One'],
    nextActions: ['Send DSCR rate sheet', 'Follow up in 3 days'],
  },
  INTEL_JENNIFER_FIRST: {
    id: CALL_INTELLIGENCE_IDS.INTEL_JENNIFER_FIRST,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    callId: VOICE_CALL_IDS.CALL_JENNIFER_FIRST,
    borrowerIntent: 'purchase',
    propertyType: 'Single Family',
    timeline: '60 days',
    loanAmountRange: { min: 280000, max: 330000 },
    qualificationScore: 65,
    qualificationFactors: ['First-time investor', 'Has capital', 'Austin market'],
    objections: ['Concerned about interest rates', 'Unfamiliar with process'],
    nextActions: ['Transfer to LO for education call', 'Send first-timer guide'],
  },
};

// =============================================================================
// AI AUTONOMY SETTINGS DATA
// =============================================================================

export const AI_AUTONOMY_SETTINGS: Record<keyof typeof AI_AUTONOMY_SETTINGS_IDS, AIAutonomySettingsSeedData> = {
  FUNDING_AUTONOMY: {
    id: AI_AUTONOMY_SETTINGS_IDS.FUNDING_AUTONOMY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    defaultAutonomyLevel: 'hybrid',
    escalationRules: [
      { trigger: 'loan_amount', condition: 'greater_than', threshold: 2000000, action: 'transfer' },
      { trigger: 'keyword', condition: 'contains', threshold: undefined, action: 'flag' },
      { trigger: 'sentiment', condition: 'equals', threshold: -0.5, action: 'notify' },
      { trigger: 'objection_count', condition: 'greater_than', threshold: 3, action: 'transfer' },
    ],
    humanAvailabilitySchedule: {
      monday: { start: '09:00', end: '18:00' },
      tuesday: { start: '09:00', end: '18:00' },
      wednesday: { start: '09:00', end: '18:00' },
      thursday: { start: '09:00', end: '18:00' },
      friday: { start: '09:00', end: '17:00' },
    },
    fallbackBehavior: 'callback',
    enableLearning: true,
  },
  SHARPE_AUTONOMY: {
    id: AI_AUTONOMY_SETTINGS_IDS.SHARPE_AUTONOMY,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    defaultAutonomyLevel: 'full_ai',
    escalationRules: [
      { trigger: 'loan_amount', condition: 'greater_than', threshold: 5000000, action: 'transfer' },
      { trigger: 'product_type', condition: 'equals', threshold: undefined, action: 'flag' },
    ],
    fallbackBehavior: 'voicemail',
    enableLearning: true,
  },
  PACIFIC_AUTONOMY: {
    id: AI_AUTONOMY_SETTINGS_IDS.PACIFIC_AUTONOMY,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    defaultAutonomyLevel: 'human_only',
    escalationRules: [],
    fallbackBehavior: 'end_call',
    enableLearning: false,
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getAllEmailCampaigns(workspace?: string): EmailCampaignSeedData[] {
  const campaigns = Object.values(EMAIL_CAMPAIGNS);
  if (!workspace) return campaigns;
  return campaigns.map((c) => ({ ...c, name: `${c.name} [${workspace}]` }));
}

export function getAllVoiceCampaigns(workspace?: string): VoiceCampaignSeedData[] {
  const campaigns = Object.values(VOICE_CAMPAIGNS);
  if (!workspace) return campaigns;
  return campaigns.map((c) => ({ ...c, name: `${c.name} [${workspace}]` }));
}

export function getAllVoiceCalls(): VoiceCallSeedData[] {
  return Object.values(VOICE_CALLS);
}

export function getAllCallIntelligence(): CallIntelligenceSeedData[] {
  return Object.values(CALL_INTELLIGENCE);
}

export function getAllAIAutonomySettings(): AIAutonomySettingsSeedData[] {
  return Object.values(AI_AUTONOMY_SETTINGS);
}

export function getEmailCampaignsByBusiness(businessId: string): EmailCampaignSeedData[] {
  return Object.values(EMAIL_CAMPAIGNS).filter((c) => c.businessId === businessId);
}

export function getVoiceCampaignsByBusiness(businessId: string): VoiceCampaignSeedData[] {
  return Object.values(VOICE_CAMPAIGNS).filter((c) => c.businessId === businessId);
}

// =============================================================================
// EMAIL CAMPAIGN RECIPIENT IDs (Issue #267)
// =============================================================================

export const EMAIL_CAMPAIGN_RECIPIENT_IDS = {
  // FUNDING_NURTURE_Q1 recipients
  RECIPIENT_MARCUS_NURTURE: seedId('email-recipient-marcus-nurture', 1),
  RECIPIENT_ROBERT_NURTURE: seedId('email-recipient-robert-nurture', 2),
  RECIPIENT_ALICIA_NURTURE: seedId('email-recipient-alicia-nurture', 3),
  RECIPIENT_JENNIFER_NURTURE: seedId('email-recipient-jennifer-nurture', 4),
  RECIPIENT_KEVIN_NURTURE: seedId('email-recipient-kevin-nurture', 5),

  // SHARPE_WELCOME_SERIES recipients
  RECIPIENT_SARAH_WELCOME: seedId('email-recipient-sarah-welcome', 1),
  RECIPIENT_RYAN_WELCOME: seedId('email-recipient-ryan-welcome', 2),
} as const;

export type EmailCampaignRecipientId = (typeof EMAIL_CAMPAIGN_RECIPIENT_IDS)[keyof typeof EMAIL_CAMPAIGN_RECIPIENT_IDS];

// =============================================================================
// COMMUNICATION PREFERENCE IDs (Issue #267)
// =============================================================================

export const COMMUNICATION_PREFERENCE_IDS = {
  // Lead communication preferences
  PREF_MARCUS_LEAD: seedId('comm-pref-marcus-lead', 1),
  PREF_ROBERT_LEAD: seedId('comm-pref-robert-lead', 2),
  PREF_ALICIA_LEAD: seedId('comm-pref-alicia-lead', 3),
  PREF_JENNIFER_LEAD: seedId('comm-pref-jennifer-lead', 4),
  PREF_KEVIN_LEAD: seedId('comm-pref-kevin-lead', 5), // Unsubscribed

  // Consumer communication preferences
  PREF_MARCUS_CONSUMER: seedId('comm-pref-marcus-consumer', 1),
  PREF_SARAH_CONSUMER: seedId('comm-pref-sarah-consumer', 2),
} as const;

export type CommunicationPreferenceId =
  (typeof COMMUNICATION_PREFERENCE_IDS)[keyof typeof COMMUNICATION_PREFERENCE_IDS];

// =============================================================================
// EMAIL CAMPAIGN RECIPIENT DATA TYPE
// =============================================================================

export interface EmailCampaignRecipientSeedData {
  id: string;
  campaignId: string;
  businessId: string;
  recipientId: string; // Lead ID or Consumer ID
  recipientEmail: string;
  status: 'pending' | 'sent' | 'delivered' | 'opened' | 'clicked' | 'bounced' | 'unsubscribed';
  sentAt?: string;
  deliveredAt?: string;
  openedAt?: string;
  clickedAt?: string;
  bouncedAt?: string;
  bounceReason?: string;
  unsubscribedAt?: string;
  createdAt: string;
}

// =============================================================================
// COMMUNICATION PREFERENCE DATA TYPE
// =============================================================================

export interface CommunicationPreferenceSeedData {
  id: string;
  businessId: string;
  entityType: 'lead' | 'consumer' | 'partner';
  entityId: string;
  email?: string;
  phone?: string;
  emailOptIn: boolean;
  smsOptIn: boolean;
  callOptIn: boolean;
  dncReason?: string;
  dncAt?: string;
  unsubscribeToken: string;
  createdAt: string;
}

// =============================================================================
// EMAIL CAMPAIGN RECIPIENT DATA
// =============================================================================

export const EMAIL_CAMPAIGN_RECIPIENTS: Record<
  keyof typeof EMAIL_CAMPAIGN_RECIPIENT_IDS,
  EmailCampaignRecipientSeedData
> = {
  // FUNDING_NURTURE_Q1 recipients - various delivery statuses
  RECIPIENT_MARCUS_NURTURE: {
    id: EMAIL_CAMPAIGN_RECIPIENT_IDS.RECIPIENT_MARCUS_NURTURE,
    campaignId: EMAIL_CAMPAIGN_IDS.FUNDING_NURTURE_Q1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    recipientId: LEAD_IDS.LEAD_MARCUS,
    recipientEmail: 'marcus.thompson@thompsoninvest.com',
    status: 'clicked',
    sentAt: '2026-01-15T09:00:00Z',
    deliveredAt: '2026-01-15T09:00:05Z',
    openedAt: '2026-01-15T10:23:00Z',
    clickedAt: '2026-01-15T10:25:00Z',
    createdAt: '2026-01-15T08:55:00Z',
  },
  RECIPIENT_ROBERT_NURTURE: {
    id: EMAIL_CAMPAIGN_RECIPIENT_IDS.RECIPIENT_ROBERT_NURTURE,
    campaignId: EMAIL_CAMPAIGN_IDS.FUNDING_NURTURE_Q1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    recipientId: LEAD_IDS.LEAD_ROBERT,
    recipientEmail: 'robert.chen@chenholdings.com',
    status: 'opened',
    sentAt: '2026-01-15T09:00:00Z',
    deliveredAt: '2026-01-15T09:00:07Z',
    openedAt: '2026-01-15T14:45:00Z',
    createdAt: '2026-01-15T08:55:00Z',
  },
  RECIPIENT_ALICIA_NURTURE: {
    id: EMAIL_CAMPAIGN_RECIPIENT_IDS.RECIPIENT_ALICIA_NURTURE,
    campaignId: EMAIL_CAMPAIGN_IDS.FUNDING_NURTURE_Q1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    recipientId: LEAD_IDS.LEAD_ALICIA,
    recipientEmail: 'alicia@martinezrealty.com',
    status: 'delivered',
    sentAt: '2026-01-15T09:00:00Z',
    deliveredAt: '2026-01-15T09:00:03Z',
    createdAt: '2026-01-15T08:55:00Z',
  },
  RECIPIENT_JENNIFER_NURTURE: {
    id: EMAIL_CAMPAIGN_RECIPIENT_IDS.RECIPIENT_JENNIFER_NURTURE,
    campaignId: EMAIL_CAMPAIGN_IDS.FUNDING_NURTURE_Q1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    recipientId: LEAD_IDS.LEAD_JENNIFER,
    recipientEmail: 'jennifer@williamsdev.com',
    status: 'bounced',
    sentAt: '2026-01-15T09:00:00Z',
    bouncedAt: '2026-01-15T09:00:12Z',
    bounceReason: 'Mailbox full - recipient quota exceeded',
    createdAt: '2026-01-15T08:55:00Z',
  },
  RECIPIENT_KEVIN_NURTURE: {
    id: EMAIL_CAMPAIGN_RECIPIENT_IDS.RECIPIENT_KEVIN_NURTURE,
    campaignId: EMAIL_CAMPAIGN_IDS.FUNDING_NURTURE_Q1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    recipientId: LEAD_IDS.LEAD_KEVIN,
    recipientEmail: 'kevin.park@parkventures.net',
    status: 'unsubscribed',
    sentAt: '2026-01-15T09:00:00Z',
    deliveredAt: '2026-01-15T09:00:04Z',
    openedAt: '2026-01-15T11:30:00Z',
    unsubscribedAt: '2026-01-15T11:31:00Z',
    createdAt: '2026-01-15T08:55:00Z',
  },

  // SHARPE_WELCOME_SERIES recipients
  RECIPIENT_SARAH_WELCOME: {
    id: EMAIL_CAMPAIGN_RECIPIENT_IDS.RECIPIENT_SARAH_WELCOME,
    campaignId: EMAIL_CAMPAIGN_IDS.SHARPE_WELCOME_SERIES,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    recipientId: LEAD_IDS.SARAH_JOHNSON,
    recipientEmail: 'sarah.johnson@jredev.com',
    status: 'clicked',
    sentAt: '2026-01-10T10:00:00Z',
    deliveredAt: '2026-01-10T10:00:08Z',
    openedAt: '2026-01-10T12:15:00Z',
    clickedAt: '2026-01-10T12:17:00Z',
    createdAt: '2026-01-10T09:55:00Z',
  },
  RECIPIENT_RYAN_WELCOME: {
    id: EMAIL_CAMPAIGN_RECIPIENT_IDS.RECIPIENT_RYAN_WELCOME,
    campaignId: EMAIL_CAMPAIGN_IDS.SHARPE_WELCOME_SERIES,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    recipientId: LEAD_IDS.RYAN_PATEL,
    recipientEmail: 'ryan.patel@outlook.com',
    status: 'delivered',
    sentAt: '2026-01-10T10:00:00Z',
    deliveredAt: '2026-01-10T10:00:05Z',
    createdAt: '2026-01-10T09:55:00Z',
  },
};

// =============================================================================
// COMMUNICATION PREFERENCE DATA
// =============================================================================

export const COMMUNICATION_PREFERENCES: Record<
  keyof typeof COMMUNICATION_PREFERENCE_IDS,
  CommunicationPreferenceSeedData
> = {
  // Lead preferences - various opt-in/out statuses
  PREF_MARCUS_LEAD: {
    id: COMMUNICATION_PREFERENCE_IDS.PREF_MARCUS_LEAD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    entityType: 'lead',
    entityId: LEAD_IDS.LEAD_MARCUS,
    email: 'marcus.thompson@thompsoninvest.com',
    phone: '+1-512-555-4001',
    emailOptIn: true,
    smsOptIn: true,
    callOptIn: true,
    unsubscribeToken: seedId('unsub-marcus-lead', 1),
    createdAt: '2026-01-01T00:00:00Z',
  },
  PREF_ROBERT_LEAD: {
    id: COMMUNICATION_PREFERENCE_IDS.PREF_ROBERT_LEAD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    entityType: 'lead',
    entityId: LEAD_IDS.LEAD_ROBERT,
    email: 'robert.chen@chenholdings.com',
    phone: '+1-713-555-4002',
    emailOptIn: true,
    smsOptIn: false,
    callOptIn: true,
    unsubscribeToken: seedId('unsub-robert-lead', 1),
    createdAt: '2026-01-01T00:00:00Z',
  },
  PREF_ALICIA_LEAD: {
    id: COMMUNICATION_PREFERENCE_IDS.PREF_ALICIA_LEAD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    entityType: 'lead',
    entityId: LEAD_IDS.LEAD_ALICIA,
    email: 'alicia@martinezrealty.com',
    phone: '+1-210-555-4003',
    emailOptIn: true,
    smsOptIn: true,
    callOptIn: false, // Prefers no calls
    unsubscribeToken: seedId('unsub-alicia-lead', 1),
    createdAt: '2026-01-01T00:00:00Z',
  },
  PREF_JENNIFER_LEAD: {
    id: COMMUNICATION_PREFERENCE_IDS.PREF_JENNIFER_LEAD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    entityType: 'lead',
    entityId: LEAD_IDS.LEAD_JENNIFER,
    email: 'jennifer@williamsdev.com',
    phone: '+1-469-555-4004',
    emailOptIn: true,
    smsOptIn: true,
    callOptIn: true,
    unsubscribeToken: seedId('unsub-jennifer-lead', 1),
    createdAt: '2026-01-01T00:00:00Z',
  },
  PREF_KEVIN_LEAD: {
    id: COMMUNICATION_PREFERENCE_IDS.PREF_KEVIN_LEAD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    entityType: 'lead',
    entityId: LEAD_IDS.LEAD_KEVIN,
    email: 'kevin.park@parkventures.net',
    phone: '+1-214-555-4005',
    emailOptIn: false, // Unsubscribed
    smsOptIn: false,
    callOptIn: false,
    dncReason: 'User unsubscribed from all communications',
    dncAt: '2026-01-15T11:31:00Z',
    unsubscribeToken: seedId('unsub-kevin-lead', 1),
    createdAt: '2026-01-01T00:00:00Z',
  },

  // Consumer preferences
  PREF_MARCUS_CONSUMER: {
    id: COMMUNICATION_PREFERENCE_IDS.PREF_MARCUS_CONSUMER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    entityType: 'consumer',
    entityId: seedId('consumer-marcus', 1), // Reference to a consumer (could be mapped if consumers existed with matching ID)
    email: 'marcus.consumer@example.com',
    phone: '+1-512-555-5001',
    emailOptIn: true,
    smsOptIn: true,
    callOptIn: true,
    unsubscribeToken: seedId('unsub-marcus-consumer', 1),
    createdAt: '2025-06-01T00:00:00Z',
  },
  PREF_SARAH_CONSUMER: {
    id: COMMUNICATION_PREFERENCE_IDS.PREF_SARAH_CONSUMER,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    entityType: 'consumer',
    entityId: seedId('consumer-sarah', 1),
    email: 'sarah.consumer@example.com',
    phone: '+1-305-555-5002',
    emailOptIn: true,
    smsOptIn: false,
    callOptIn: true,
    unsubscribeToken: seedId('unsub-sarah-consumer', 1),
    createdAt: '2025-07-01T00:00:00Z',
  },
};

// =============================================================================
// EMAIL CAMPAIGN RECIPIENT HELPER FUNCTIONS
// =============================================================================

export function getAllEmailCampaignRecipients(): EmailCampaignRecipientSeedData[] {
  return Object.values(EMAIL_CAMPAIGN_RECIPIENTS);
}

export function getEmailCampaignRecipientsByCampaign(campaignId: string): EmailCampaignRecipientSeedData[] {
  return Object.values(EMAIL_CAMPAIGN_RECIPIENTS).filter((r) => r.campaignId === campaignId);
}

export function getEmailCampaignRecipientsByBusiness(businessId: string): EmailCampaignRecipientSeedData[] {
  return Object.values(EMAIL_CAMPAIGN_RECIPIENTS).filter((r) => r.businessId === businessId);
}

export function getEmailCampaignRecipientsByStatus(
  status: EmailCampaignRecipientSeedData['status'],
): EmailCampaignRecipientSeedData[] {
  return Object.values(EMAIL_CAMPAIGN_RECIPIENTS).filter((r) => r.status === status);
}

// =============================================================================
// COMMUNICATION PREFERENCE HELPER FUNCTIONS
// =============================================================================

export function getAllCommunicationPreferences(): CommunicationPreferenceSeedData[] {
  return Object.values(COMMUNICATION_PREFERENCES);
}

export function getCommunicationPreferencesByBusiness(businessId: string): CommunicationPreferenceSeedData[] {
  return Object.values(COMMUNICATION_PREFERENCES).filter((p) => p.businessId === businessId);
}

export function getCommunicationPreferencesByEntityType(
  entityType: CommunicationPreferenceSeedData['entityType'],
): CommunicationPreferenceSeedData[] {
  return Object.values(COMMUNICATION_PREFERENCES).filter((p) => p.entityType === entityType);
}

export function getDncPreferences(): CommunicationPreferenceSeedData[] {
  return Object.values(COMMUNICATION_PREFERENCES).filter((p) => p.dncAt !== undefined);
}
